import React, { useContext, useEffect, useState } from 'react';
import { useFetchRequiredCredits, useGenerateAiMedia } from 'apps/t2c-fe/services';
import { useSnackbar } from 'notistack';
import { useDialog } from '../useDialog';

import { useRouter } from 'next/router';
import { useCreditContext } from '../useCreditContext';
import {
  AiMusicEnum,
  AUDIO_DEFAULT_NAME_PREFIX,
  getRandomInt,
  MUSIC_DURATION_LIMITS,
} from '../../utils';
import { SelectChangeEvent } from '@mui/material/Select';
import { isCreditsAvailable } from '../../utils/utilities';
import { AudioCredit } from '../../@types';
import { useAppContext } from '../context';
import { useEnhancePrompt } from '../broll/useEnchancePrompt';

export const useMusicPage = ({ refetchmusic }) => {
  const [musicGenerated, setmusicGenerated] = useState(false);
  const [influenceType, setInfluenceType] = useState<AiMusicEnum>(AiMusicEnum.FOCUS);
  const [creditsData, setCreditsData] = useState<AudioCredit | null>(null);

  const [musicGenerationData, setmusicGenerationData] = useState<{
    prompt: string;
    name: string;
    duration: number | '';
    influence: number;
  }>({
    prompt: '',
    name: AUDIO_DEFAULT_NAME_PREFIX + getRandomInt(1, 1000),
    duration: 5,
    influence: 0.9,
  });
  const [musicGenerationError, setMusicGenerationError] = useState<string | null>(null);

  const supportedFormats = ['mp4', 'jpg', 'jpeg', 'png'];

  const { enqueueSnackbar } = useSnackbar();

  const handleOutputTypeChange = (event: SelectChangeEvent<AiMusicEnum>) => {
    setInfluenceType(event.target.value as AiMusicEnum);
    handleDataChange('influence', event.target.value);
  };

  const handleInfluenceChange = (value: string) => {
    return value === AiMusicEnum.FOCUS ? 0.9 : 0.5;
  };

  const {
    mutateAsync: mutateFetchRequiredCredits,
    isLoading: isFetchingRequiredCredits,
    data: requiredCreditsData,
    isError: isCreditFetchError,
  } = useFetchRequiredCredits();

  const {
    closeDialog: closeCreditDialog,
    isDialogOpen: isCreditDialogOpen,
    openDialog: openCreditDialog,
  } = useDialog();

  const {
    closeDialog: closeProccessingModal,
    isDialogOpen: isProccessingModalOpen,
    openDialog: openProccessingModal,
  } = useDialog();

  const { mutateAsync: generateAimusic, isLoading: musicGenerationLoading } = useGenerateAiMedia();

  const { mutateAsync: mutateEnhancePrompt, isLoading: isEnhancing } = useEnhancePrompt();

  const { push } = useRouter();
  const { setUsableCredits, usableCredits } = useCreditContext();
  const { showCreditDialog } = useAppContext();

  // Keeps duration a whole number within limits; empty is allowed only while the user is typing
  const clampDuration = (value: string): number | '' => {
    const digits = value.replace(/\D/g, '');
    if (!digits) return '';
    return Math.min(
      MUSIC_DURATION_LIMITS.MAX,
      Math.max(MUSIC_DURATION_LIMITS.MIN, parseInt(digits))
    );
  };

  const handleDurationBlur = () => {
    if (musicGenerationData.duration === '') {
      setmusicGenerationData({ ...musicGenerationData, duration: MUSIC_DURATION_LIMITS.MIN });
    }
  };

  const handleDurationKeyDown = (event: React.KeyboardEvent) => {
    if (['e', 'E', '+', '-', '.', ','].includes(event.key)) event.preventDefault();
  };

  const handleDataChange = (key: string, value: string) => {
    if (key === 'duration')
      setmusicGenerationData({ ...musicGenerationData, duration: clampDuration(value) });
    else if (value && key === 'influence')
      setmusicGenerationData({ ...musicGenerationData, [key]: handleInfluenceChange(value) });
    else setmusicGenerationData({ ...musicGenerationData, [key]: value });
  };

  const handleMusicGenerate = async () => {
    let errMsg = '';
    const { duration } = musicGenerationData;
    if (
      duration === '' ||
      !Number.isInteger(duration) ||
      duration < MUSIC_DURATION_LIMITS.MIN ||
      duration > MUSIC_DURATION_LIMITS.MAX
    )
      errMsg = `Duration must be a whole number between ${MUSIC_DURATION_LIMITS.MIN} and ${MUSIC_DURATION_LIMITS.MAX} seconds.`;
    if (!musicGenerationData.name.trim()) errMsg = 'Please Enter The Name.';
    if (!musicGenerationData.prompt.trim()) errMsg = 'Please Enter A Prompt.';

    if (errMsg) {
      enqueueSnackbar(errMsg, { variant: 'error' });
      return;
    }

    const res = await mutateFetchRequiredCredits({
      aiGenType: 'audio',
      duration: Number(duration),
    });
    setCreditsData(res);
  };

  useEffect(() => {
    if (creditsData) {
      const isEnoughCredit = isCreditsAvailable(
        creditsData?.availableCredits,
        creditsData?.creditsInUse,
        creditsData?.requiredCredits
      );
      if (showCreditDialog) {
        openCreditDialog();
      } else {
        if (!isFetchingRequiredCredits && isEnoughCredit) {
          handleConfirmGeneration();
        } else if (!isEnoughCredit) {
          // handleUpgrade();
          openCreditDialog();
        }
      }
    }
  }, [creditsData]);

  const handleConfirmGeneration = async () => {
    try {
      closeCreditDialog();
      openProccessingModal();
      const sanitizedData = {
        ...musicGenerationData,
        name: musicGenerationData.name.trim(),
        prompt: musicGenerationData.prompt.trim(),
        duration: Number(musicGenerationData.duration) || MUSIC_DURATION_LIMITS.MIN,
      };
      const res = await generateAimusic({
        category: 'audio',
        ...sanitizedData,
      });
      setUsableCredits(usableCredits - creditsData.requiredCredits);
      await refetchmusic();
      push(`music/${res.id}`);
    } catch (error) {
      setMusicGenerationError(error?.response?.data?.message);
    }
  };

  const handlePromptEnhance = async () => {
    try {
      const res = await mutateEnhancePrompt({
        prompt: musicGenerationData.prompt,
        model: 'audio',
      });
      handleDataChange('prompt', res);
    } catch (error) {
      enqueueSnackbar('Failed to enchance prompt', { variant: 'error' });
    }
  };

  const handleUpgrade = () => push('/subscription');
  return {
    musicGenerated,
    setmusicGenerated,
    supportedFormats,
    musicGenerationData,
    handleDataChange,
    handleDurationBlur,
    handleDurationKeyDown,
    influenceType,
    handleOutputTypeChange,
    handleMusicGenerate,
    isCreditDialogOpen,
    closeCreditDialog,
    isFetchingRequiredCredits,
    requiredCreditsData,
    handleConfirmGeneration,
    musicGenerationLoading,
    isProccessingModalOpen,
    closeProccessingModal,
    handleUpgrade,
    musicGenerationError,
    isEnhancing,
    handlePromptEnhance,
  };
};
